import { useEffect, useState } from "react";

import { useMedplum } from "@medplum/react";

import {
  loadClientBillingPaymentMethod,
  loadPrimaryCoveragePayerDisplay,
} from "@/billing/services/client-billing-payment-method";

import type { LoadClientBillingPaymentMethodResult } from "../types/billing-payment-method";

export interface UseClientBillingPaymentMethodResult {
  paymentMethodResult: LoadClientBillingPaymentMethodResult | undefined;
  insuranceDisplay: string | undefined;
  isLoading: boolean;
  isPaymentMethodUndetermined: boolean;
  paymentMethodDisplay: string;
}

/**
 * Loads Intake billing payment method and primary Coverage payor display for a client.
 */
export function useClientBillingPaymentMethod(
  clientId: string | undefined,
): UseClientBillingPaymentMethodResult {
  const medplum = useMedplum();
  const [paymentMethodResult, setPaymentMethodResult] = useState<
    LoadClientBillingPaymentMethodResult | undefined
  >(undefined);
  const [insuranceDisplay, setInsuranceDisplay] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(Boolean(clientId));

  useEffect(() => {
    if (!clientId) {
      setPaymentMethodResult(undefined);
      setInsuranceDisplay(undefined);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const load = async () => {
      const [paymentResult, payerDisplay] = await Promise.all([
        loadClientBillingPaymentMethod(medplum, clientId),
        loadPrimaryCoveragePayerDisplay(medplum, clientId),
      ]);
      if (cancelled) return;
      setPaymentMethodResult(paymentResult);
      setInsuranceDisplay(payerDisplay);
      setIsLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [medplum, clientId]);

  // load_failed is treated like undetermined for Send gating — cannot safely bill without a method.
  const isPaymentMethodUndetermined =
    !isLoading && (paymentMethodResult === undefined || paymentMethodResult.ok === false);

  const paymentMethodDisplay =
    paymentMethodResult?.ok === true ? paymentMethodResult.data.displayLabel : "--";

  return {
    paymentMethodResult,
    insuranceDisplay,
    isLoading,
    isPaymentMethodUndetermined,
    paymentMethodDisplay,
  };
}
